<template>
  <div class="buy-feature">
    <div class="header">
      <div class="header__texts">
        <span class="header__title"> {{ $t('xuan-ze-jia-gou-rong-liang') }} </span>
        <span class="header__subtitle">
          {{ $t('gou-mai-hou-you-xiao-qi-zhi-expireformat', [expireFormat()]) }}
        </span>
      </div>
    </div>
    <div class="buy-feature__desc">
      {{ $t('jia-gou-cun-chu-kong-jian-gou-mai-cheng-gong-hou-li-ji-sheng-xiao') }}
    </div>
    <div class="product_list">
      <div
        v-for="item of packageList"
        :key="item.id"
        class="product_item"
        :class="{
          selected: item.id === selection.current?.id,
        }"
        @click="onSelectPackage(item)"
      >
        <div class="package-amount">
          <span class="package-amount__text"> 1{{ item.type }} </span>
        </div>
        <div class="price-row">
          <div class="product_price">
            <span class="product_price__currency">
              {{ formatPrice(item.price).prefix }}
            </span>
            <span class="product_price__value">
              {{ formatPrice(item.price).value }}
            </span>
            <span class="product_price__period">/{{ $t('nian') }}</span>
          </div>
          <div class="old-price">
            {{ item.oldPrice }}
          </div>
        </div>
        <div class="quantity-row">
          <div class="buy-label">{{ $t('gou-mai') }}</div>
          <div class="quantity-input" @click.stop.prevent="onSelectPackage(item)">
            <InputNumber
              type="number"
              :value="quantityById[item.id]"
              :min="item.min"
              :step="1"
              :formatter="formatIntegerInput"
              :max="item.max"
              :default-value="1"
              inputmode="numeric"
              pattern="\d*"
              :placeholder="$t('qing-shu-ru-shu-liang')"
              autocomplete="off"
              :precision="0"
              @change="onQuantityChange"
            />
          </div>
          <div class="unit">
            {{ item.type }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts" name="StorageInfo">
import { RIGHTS_FEATURE } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import { InputNumber } from '@rendora/design/components/input/index';
import dayjs from 'dayjs';
import { onMounted, reactive, ref, watch } from 'vue';

interface StoragePackageItem {
  id: number;
  type: string;
  price: number;
  oldPrice?: string | number;
  min: number;
  max: number;
}

const props = withDefaults(
  defineProps<{
    rightsName: string;
    featureType: RIGHTS_FEATURE;
    packageList: StoragePackageItem[];
    count: number;
    currentPackage?: StoragePackageItem | null;
  }>(),
  {
    packageList: () => [],
  },
);

const emits = defineEmits<{
  (e: 'update:count', value: number): void;
  (e: 'update:currentPackage', value: StoragePackageItem): void;
}>();

// 按套餐 ID 存储的数量映射
const quantityById = ref<Record<number, number>>({});

// 当前选中的套餐与数量
const selection = reactive<{ count: number; current: StoragePackageItem | null | undefined }>({
  count: props.count || 1,
  current: props?.currentPackage,
});

// 工具方法
const parseNumber = (v: string | number): number => {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

const clampToBounds = (n: number, min: number, max: number): number => {
  if (!Number.isFinite(n)) return min;
  if (n < min) return min;
  if (n > max) return max;
  return Math.trunc(n);
};

// 输入框格式化：仅保留正整数的显示
const formatIntegerInput = (v: string | number): string => {
  const n = Number.parseInt(String(v ?? '1'));
  return Number.isFinite(n) && n > 0 ? String(n) : '1';
};

const expireFormat = () => dayjs().add(1, 'year').format('YYYY.MM.DD');

// 当套餐列表变更时，初始化各套餐默认数量
watch(
  () => props.packageList,
  (list: StoragePackageItem[]) => {
    const next: Record<number, number> = {};
    list.forEach((item: StoragePackageItem) => {
      // default to 1 within [min, max]
      const initial = Math.min(Math.max(1, item.min), item.max);
      next[item.id] = initial;
    });
    quantityById.value = next;

    // 保证当前选中项与传入的列表保持一致
    if (selection.current) {
      const found = list.find((p: StoragePackageItem) => p.id === selection.current!.id);
      if (!found && list.length > 0) {
        selection.current = list[0];
        selection.count = quantityById.value[selection.current.id] ?? 1;
        emits('update:currentPackage', selection.current);
        emits('update:count', selection.count);
      }
    } else if (list.length > 0) {
      selection.current = list[0];
      selection.count = quantityById.value[selection.current.id] ?? 1;
      emits('update:currentPackage', selection.current);
      emits('update:count', selection.count);
    }
  },
  { immediate: true },
);

// 事件处理
const onSelectPackage = (pkg: StoragePackageItem) => {
  if (!selection.current || pkg.id !== selection.current.id) {
    selection.current = pkg;
    const nextCount = quantityById.value[pkg.id] ?? 1;
    selection.count = clampToBounds(nextCount, pkg.min, pkg.max);
    emits('update:currentPackage', pkg);
    emits('update:count', selection.count);
  }
};

const onQuantityChange = (value: string | number) => {
  if (!selection.current) return;
  const raw = parseNumber(value);
  const next = clampToBounds(raw, selection.current.min, selection.current.max);
  quantityById.value[selection.current.id] = next;
  selection.count = next;
  emits('update:count', next);
};

onMounted(() => {
  if (props?.currentPackage) {
    selection.current = props.currentPackage;
    const id = props.currentPackage.id;
    if (quantityById.value[id] == null) {
      quantityById.value[id] = clampToBounds(1, props.currentPackage.min, props.currentPackage.max);
    }
    selection.count = quantityById.value[id] ?? 1;
    emits('update:count', selection.count);
  }
});
</script>

<style lang="scss" scoped>
.quantity-input {
  border: 1px solid var(--x-back-5);
  font-size: 14px;
  border-radius: var(--x-radius-medium);
  background-color: var(--x-white-1);
  &:deep(input) {
    border: none;
    padding: 0;
    font-size: 14px;
    padding: 8px 12px;
    border-radius: var(--x-radius-medium);
    height: 100%;
    width: 100%;
    box-sizing: border-box;
  }
}
.buy-feature {
  padding: 24px;

  .header {
    display: flex;
    align-items: center;

    &__texts {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      line-height: 20px;
    }

    &__title {
      font-size: 16px;
      font-weight: var(--x-weight-medium);
      color: var(--x-text-1);
    }

    &__subtitle {
      font-size: 12px;
      color: var(--x-text-7);
    }
  }

  &__desc {
    padding-top: 12px;
    padding-bottom: 20px;
    color: var(--x-text-7);
    font-size: 12px;
    line-height: 18px;
  }

  &__block {
    margin-top: 30px;
    display: flex;
    align-items: center;

    &--label {
      width: 48px;
      opacity: 0.6;
    }
  }

  .product_list {
    display: grid;
    min-width: 400px;
    grid-template-columns: repeat(auto-fill, 232px);
    gap: 16px;
    justify-content: start;

    .product_item {
      width: 232px;
      height: 203px;
      box-sizing: border-box;
      background: var(--x-white-1);
      border-radius: var(--x-radius-large);
      padding: 20px;
      transition:
        border-color 0.2s ease,
        background-color 0.2s ease,
        box-shadow 0.2s ease;
      position: relative;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      border: 1px solid var(--x-back-5);

      &:hover,
      &.selected {
        border-color: var(--x-purple-5);
        background: var(--x-purple-1);
      }

      .selected-icon {
        position: absolute;
        top: 10px;
        right: 10px;
      }

      .package-amount {
        .package-amount__text {
          display: inline-block;
          font-size: 18px;
          line-height: 26px;
          border-radius: 26px;
          color: var(--x-text-1);
          font-weight: 600;
        }
      }

      .price-row {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
        padding: 10px 0 6px;

        .product_price {
          font-size: 16px;

          &__period {
            color: var(--x-text-1);
            opacity: 0.6;
            margin-left: 4px;
          }
        }

        .old-price {
          font-size: 12px;
          color: var(--x-text-1);
          opacity: 0.4;
          text-decoration: line-through;
          text-align: center;
        }
      }

      .quantity-row {
        display: flex;
        align-items: center;
        padding-top: 6px;

        .buy-label {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: var(--x-text-1);
          opacity: 0.7;
        }

        .quantity-input {
          margin-left: 12px;
          margin-right: 8px;
          width: 100px;

          :deep(.ant-input-number) {
            width: 100%;
          }
        }

        .unit {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: var(--x-text-1);
          opacity: 0.7;
        }
      }
    }
  }
}
</style>
